/**
 * Pure rank/exp math for the achievements & ranks system — the ONE place the
 * tier boundaries, thresholds and carryover rules live. Every consumer
 * (RankService, applyGive, the Achievements screen) derives from here.
 * Spec: docs/superpowers/specs/2026-08-25-achievements-ranks-design.md
 */

export const MAX_RANK = 40;

export type RankTier = 'bronze' | 'silver' | 'gold' | 'diamond';

/** Ranks 1–9 bronze, 10–19 silver, 20–30 gold, 31–40 diamond (2026-10-29). */
export function tierForRank(rank: number): RankTier {
  return rank >= 31 ? 'diamond' : rank >= 20 ? 'gold' : rank >= 10 ? 'silver' : 'bronze';
}

/** Exp needed to leave `rank` (0 at MAX_RANK — no further progress). */
export function expToNext(rank: number): number {
  if (rank >= MAX_RANK) return 0;
  const tier = tierForRank(rank);
  return tier === 'diamond' ? 1250 : tier === 'gold' ? 1000 : tier === 'silver' ? 750 : 500;
}

export function clampRank(n: number): number {
  return Math.max(1, Math.min(MAX_RANK, Math.floor(n)));
}

/** Apply gained exp with carryover; exp at MAX_RANK is discarded. */
export function addExp(rank: number, exp: number, gained: number): { rank: number; exp: number } {
  let r = clampRank(rank);
  let e = Math.max(0, Math.floor(exp)) + Math.max(0, Math.floor(gained));
  while (r < MAX_RANK && e >= expToNext(r)) {
    e -= expToNext(r);
    r += 1;
  }
  if (r >= MAX_RANK) e = 0;
  return { rank: r, exp: e };
}
